import { useEffect, useState, type ReactNode } from 'react';
import type { ScheduleEntry, ScheduleSettings } from '@guest/shared';
import { guestApi } from '../lib/api.js';
import { Icon } from '../lib/icons.js';
import { AppBar } from '../components/Chrome.js';
import { Panel } from '../components/ui.js';
import { pretty, toMinutes, toTime } from '../lib/clock.js';

function TimeRow({
  label,
  icon,
  value,
  onChange,
}: {
  label: string;
  icon: string;
  value: string;
  onChange: (next: string) => void;
}): ReactNode {
  return (
    <div className="time-row">
      <span className="ctrl-icon" data-on={true}>
        <Icon name={icon} size={18} />
      </span>
      <span className="time-label">{label}</span>
      <div className="stepper">
        <button
          type="button"
          aria-label={`${label} 15 minutes earlier`}
          onClick={() => onChange(toTime(toMinutes(value) - 15))}
        >
          −
        </button>
        <span className="stepper-value time">{pretty(value)}</span>
        <button
          type="button"
          aria-label={`${label} 15 minutes later`}
          onClick={() => onChange(toTime(toMinutes(value) + 15))}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * One entry, one card. The two settings are deliberately not merged: a guest
 * choosing when their own room wakes up is a private preference, while changing
 * when the whole house dims affects everyone in it. Same shape, different
 * weight — so separate cards, separate wording, separate save.
 */
function EntryCard({
  entry,
  onSaved,
}: {
  entry: ScheduleEntry;
  onSaved: (next: ScheduleSettings) => void;
}): ReactNode {
  const house = entry.kind === 'house_night';
  const [draft, setDraft] = useState({ wake: entry.wake, bed: entry.bed });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft({ wake: entry.wake, bed: entry.bed });
  }, [entry.wake, entry.bed]);

  const changed = draft.wake !== entry.wake || draft.bed !== entry.bed;
  const room = (entry.roomName ?? 'your room').toLowerCase();

  const run = async (fn: () => Promise<ScheduleSettings>): Promise<void> => {
    setBusy(true);
    try {
      onSaved(await fn());
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title={house ? 'Evening lighting' : 'When your room wakes up'}
      icon={house ? 'moon' : 'sun'}
      className="stack"
    >
      <p className="schedule-intro">
        {house
          ? 'The whole house switches to softer, warmer light for the evening, and back to bright in the morning.'
          : `Between these times, walking into ${room} turns the lights on for you. Outside them nothing comes on by itself, so a light never wakes you.`}
      </p>

      <div className="time-rows">
        <TimeRow
          label={house ? 'Bright by' : 'Up at'}
          icon="sun"
          value={draft.wake}
          onChange={(wake) => setDraft({ ...draft, wake })}
        />
        <TimeRow
          label={house ? 'Soft by' : 'Bed at'}
          icon="moon"
          value={draft.bed}
          onChange={(bed) => setDraft({ ...draft, bed })}
        />
      </div>

      {changed ? (
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={busy}
          onClick={() => void run(() => guestApi.setSchedule({ kind: entry.kind, ...draft }))}
        >
          <Icon name="check" size={17} />
          {busy ? 'Saving…' : 'Save'}
        </button>
      ) : (
        <p className="schedule-state">
          {saved ? (
            <>
              <Icon name="check" size={14} /> Saved
            </>
          ) : entry.overriding ? (
            <>
              <Icon name="check" size={14} /> Your times.{' '}
              <button
                type="button"
                className="linkish"
                disabled={busy}
                onClick={() => void run(() => guestApi.clearSchedule(entry.kind))}
              >
                Go back to the usual ones
              </button>
            </>
          ) : (
            <>
              Using the usual times ({pretty(entry.defaultWake)} – {pretty(entry.defaultBed)}).
            </>
          )}
        </p>
      )}

      {house && (
        <p className="wifi-note">
          The evening change actually happens at sunset when that is earlier, so setting a later time
          makes no difference until the nights get shorter.
        </p>
      )}
    </Panel>
  );
}

export function Timings(): ReactNode {
  const [settings, setSettings] = useState<ScheduleSettings | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    void guestApi
      .schedule()
      .then(setSettings)
      .catch(() => setError(true));
  }, []);

  if (error) return <div className="empty">These settings are not available to you.</div>;
  if (!settings) return <div className="empty">Loading…</div>;

  return (
    <>
      <AppBar title="Lighting times" subtitle="While you are staying" back />
      <main className="shell stack" style={{ gap: 'var(--space-3)' }}>
        {settings.entries.length === 0 && <div className="empty">Nothing to set.</div>}
        {settings.entries.map((entry) => (
          <EntryCard key={entry.kind} entry={entry} onSaved={setSettings} />
        ))}
      </main>
    </>
  );
}
